import { useEffect, useRef } from 'react';
import { delayBotAction, delayBotQuickFold, pauseMs } from '@/ai/thinking';
import type { Bot } from '@/engine/ports';
import { createRng } from '@/engine/rng';
import { legalActions } from '@/engine/rules';
import type { PlayerId, Street } from '@/engine/types';
import { useGameStore } from '@/state/gameStore';

const STREET_ADVANCE_PAUSE_MS = 700;

export type UseBotDriverOptions = {
  heroId: PlayerId;
  /** Maps a playerId to a cached Bot instance for that seat. */
  getBot: (playerId: PlayerId) => Bot;
};

/**
 * Runs a bot's turn whenever the engine hands it control.
 *
 * `street` is in the deps list — not because the effect body reads it
 * directly, but because a hand can narrow so that the same player is
 * both the last actor preflop AND the first actor postflop. A bare
 * `pendingActor` dep would miss the street crossing and the bot would
 * sit forever. The `void street` read below makes the dep load-bearing
 * under `useExhaustiveDependencies` lint.
 */
export function useBotDriver({ heroId, getBot }: UseBotDriverOptions): void {
  const status = useGameStore((s) => s.status);
  const pendingActor = useGameStore((s) => s.hand?.pendingActor ?? null);
  const street = useGameStore((s) => s.hand?.betting.street ?? null);
  const prevStreet = useRef<Street | null>(null);
  const delayRng = useRef(createRng('bot-driver-delay'));

  useEffect(() => {
    prevStreet.current = street;
  }, [street]);

  useEffect(() => {
    if (status !== 'awaiting-action') return;
    if (!pendingActor || pendingActor === heroId) return;
    void street; // keep `street` load-bearing: forces re-fire across street change (see header doc)
    let cancelled = false;
    (async () => {
      const handBefore = useGameStore.getState().hand;
      if (!handBefore || handBefore.pendingActor !== pendingActor) return;

      if (
        prevStreet.current !== null &&
        handBefore.betting.street !== prevStreet.current &&
        handBefore.betting.street !== 'preflop'
      ) {
        await pauseMs(STREET_ADVANCE_PAUSE_MS);
        if (cancelled) return;
      }

      // Decide first, then apply the cosmetic delay — lets the delay
      // adapt to the action kind. Preflop folds use a shorter beat so
      // early "nobody plays" rounds don't slog; everything else keeps
      // the standard thinking cadence. GTO decide is synchronous so
      // swapping the order has no perceived latency impact.
      const hand = useGameStore.getState().hand;
      if (!hand || hand.pendingActor !== pendingActor) return;
      const legals = legalActions(hand, pendingActor);
      if (legals.length === 0) return;
      const decided = await getBot(pendingActor).decide(hand, pendingActor, legals);
      if (cancelled) return;

      const isQuickPreflopFold = hand.betting.street === 'preflop' && decided.type === 'fold';
      if (isQuickPreflopFold) {
        await delayBotQuickFold(delayRng.current);
      } else {
        await delayBotAction(delayRng.current);
      }
      if (cancelled) return;

      await useGameStore.getState().applyAction(decided);
    })();
    return () => {
      cancelled = true;
    };
  }, [status, pendingActor, street, heroId, getBot]);
}
